import type { AxiosError } from 'axios';

interface ApiErrorData {
  error?: string | { message?: string };
  message?: string;
  errors?: string | string[];
}

/**
 * Extracts a human-readable error message from an Axios error response.
 *
 * API error shapes handled:
 *  - { error: { message: "..." } }
 *  - { error: "string" }
 *  - { message: "..." }
 *  - { errors: "..." | ["..."] }
 *
 * Falls back to the generic Axios message ("Request failed with status code 500")
 * only when none of the above are present.
 */
export function getApiErrorMessage(error: unknown, fallback = 'An unexpected error occurred'): string {
  if (!error) return fallback;

  // Axios error with response body
  const axiosError = error as AxiosError<ApiErrorData>;
  const data = axiosError?.response?.data;

  if (data) {
    // { error: { message: "The email address already exists" } }
    if (typeof data.error === 'object' && data.error?.message) {
      return data.error.message;
    }

    // { error: "invalid_grant" }
    if (typeof data.error === 'string' && data.error) {
      return data.error;
    }

    // { message: "Something went wrong" }
    if (typeof data.message === 'string' && data.message) {
      return data.message;
    }

    // { errors: ["..."] }
    if (data.errors) {
      if (Array.isArray(data.errors)) return data.errors.join(', ');
      if (typeof data.errors === 'string') return data.errors;
    }
  }

  // Plain Error object (non-Axios) — use its message
  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
}
